// Corpo de ação em texto puro, exibido num container que espera HTML.
//
// A importação do Movidesk trouxe 262 mil chamados, e o corpo de cada ação vem
// como TEXTO, não HTML: `htmlDescription` só existe em
// `/tickets/past/htmldescription`, um chamado por vez -- 262 mil requisições
// extras, 437 horas no limite de 10/min deles. O `description` da listagem já
// traz o conteúdo completo, só que com as tags removidas e as quebras de linha
// preservadas como `\n`.
//
// O histórico do chamado renderiza com `dangerouslySetInnerHTML`, e o HTML
// colapsa `\n` em espaço. Resultado: a lista de 20 pontos da ação vira um
// parágrafo corrido, e o histórico de conversa do WhatsApp vira um bloco único
// de várias telas. É o que se vê no chamado #238654.
//
// A conversão é feita na RENDERIZAÇÃO, não na gravação -- mesma decisão do
// `sanitizeHtml`: o banco continua com o texto original, e a correção vale para
// tudo o que já está gravado, sem reimportar nada.

/** Uma tag de verdade: `<` seguido de letra (ou `</`), fechando em algum `>`. */
const TAG = /<\/?[a-z][\s\S]*?>/i;

/**
 * O conteúdo é texto puro, e não HTML?
 *
 * `estoque < 10` continua sendo texto: só conta como tag o `<` seguido de
 * letra. O contrário -- tratar HTML de verdade como texto -- mostraria as tags
 * na tela do operador, então a dúvida sempre pende para "é HTML".
 *
 * @param {string} conteudo corpo da ação, como veio da API
 * @returns {boolean}
 */
export const looksLikePlainText = (conteudo) => {
  if (!conteudo) return false;

  return !TAG.test(String(conteudo));
};

/**
 * Converte o texto puro em HTML preservando a estrutura de linhas.
 *
 * @param {string} texto corpo da ação em texto puro
 * @returns {string} HTML com `<br>` no lugar das quebras
 */
export const plainTextToHtml = (texto) => {
  if (!texto) return '';

  const linhas = String(texto)
    .replace(/\r\n?/g, '\n')
    // A indentação aqui não é do autor: é o recuo do HTML original (a tabela do
    // histórico de conversa chega com 20+ espaços por linha). Mantê-la jogaria
    // o texto todo para o meio da tela.
    .split('\n')
    .map((linha) => linha.trim())
    .join('\n')
    // A mesma tabela deixa dezenas de linhas vazias entre uma célula e outra.
    // Uma linha em branco separa; cinco só empurram o texto para fora da tela.
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  if (linhas === '') return '';

  // Só `<` e `>`: o `&` fica como está de propósito. O Movidesk manda `&nbsp;`
  // dentro do texto puro, e escapar o `&` mostraria "&nbsp;" literal na tela.
  // Sem `<`, nenhuma tag se forma -- e o DOMPurify ainda roda depois disto.
  const escapado = linhas.replace(/</g, '&lt;').replace(/>/g, '&gt;');

  // `<br>`, e não `<p>`: o container aplica `p { margin: 0 }` por causa do
  // editor Quill, então parágrafo não separaria nada visualmente.
  return escapado.replace(/\n/g, '<br>');
};

/**
 * Devolve o conteúdo pronto para renderizar: converte se for texto, devolve
 * intacto se já for HTML.
 *
 * @param {string} conteudo corpo da ação
 * @returns {string}
 */
export const ticketCommentToHtml = (conteudo) => {
  if (!conteudo) return '';

  return looksLikePlainText(conteudo) ? plainTextToHtml(conteudo) : String(conteudo);
};

export default ticketCommentToHtml;
